import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function QuadraticFactoredPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>Jeżeli funkcja kwadratowa ma miejsca zerowe, możemy zapisać ją w postaci iloczynowej:</p>
      <BlockMath math="f(x)=a(x-x_1)(x-x_2)" />
      <p>
        Liczby <InlineMath math="x_1" /> i <InlineMath math="x_2" /> są miejscami
        zerowymi, bo dla każdej z nich jeden z czynników jest równy zero. Znak
        współczynnika <InlineMath math="a" /> nadal określa kierunek ramion paraboli.
      </p>
      <p>
        Gdy znamy postać ogólną <InlineMath math="ax^2+bx+c" />, najpierw liczymy
        deltę i miejsca zerowe:
      </p>
      <BlockMath math={String.raw`\Delta=b^2-4ac`} />
      <BlockMath math={String.raw`x_1=\frac{-b-\sqrt{\Delta}}{2a}\qquad x_2=\frac{-b+\sqrt{\Delta}}{2a}`} />
      <p>
        Dla <InlineMath math="\Delta>0" /> otrzymujemy dwa
        czynniki, a dla <InlineMath math="\Delta=0" /> jeden czynnik podniesiony do kwadratu:
      </p>
      <BlockMath math={String.raw`f(x)=a(x-x_0)^2`} />
      <p>
        Oś symetrii leży dokładnie pomiędzy miejscami zerowymi:
        {" "}<InlineMath math={String.raw`p=\frac{x_1+x_2}{2}`} />. Jest to szczególny
        przypadek ogólnej własności: argumenty, dla których funkcja kwadratowa
        przyjmuje tę samą wartość, leżą symetrycznie po obu stronach osi.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Odczytywanie miejsc zerowych",
      description: "Znaki liczb w nawiasach zmieniają się przy odczytywaniu x₁ i x₂.",
      content: (
        <div className="space-y-3">
          <BlockMath math="f(x)=-2(x-4)(x+1)" />
          <p>
            Mamy <InlineMath math="a=-2" />, <InlineMath math="x_1=4" /> oraz
            <InlineMath math="x_2=-1" />. Oś symetrii ma równanie:
          </p>
          <BlockMath math={String.raw`x=\frac{4+(-1)}{2}=\frac32`} />
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Od postaci iloczynowej do ogólnej",
      description: "Mnożymy nawiasy, a następnie każdy wyraz przez współczynnik a.",
      content: (
        <div className="space-y-3">
          <BlockMath math={String.raw`f(x)=2(x-3)(x+2)`} />
          <BlockMath math={String.raw`f(x)=2(x^2-x-6)=2x^2-2x-12`} />
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Od postaci ogólnej do iloczynowej",
      description: "Wyznaczamy miejsca zerowe i podstawiamy je do wzoru iloczynowego.",
      content: (
        <div className="space-y-3">
          <BlockMath math="f(x)=x^2-x-6" />
          <BlockMath math={String.raw`\Delta=(-1)^2-4\cdot1\cdot(-6)=25`} />
          <BlockMath math={String.raw`x_1=3\qquad x_2=-2`} />
          <BlockMath math="f(x)=(x-3)(x+2)" />
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
